"use client";

import { grantConsent, revokeConsent } from "@nurani-io/website-kit/consent";
import { useConsentState } from "@nurani-io/website-kit/consent-banner";
import { useLocale, useTranslations } from "next-intl";
import { recordConsentDecision } from "@/lib/record-consent";
import { capturedAttribution } from "@/lib/marketing-attribution";

/**
 * The withdrawal route. Linked from the footer of every page, in every
 * locale, and anchored at /privacy#cookievoorkeuren.
 *
 * Withdrawing consent has to be as easy as giving it (AVG Art 7(3)). So both
 * buttons are always here, always the same size, and the current answer is
 * stated in words above them; there is no "manage preferences" maze and no
 * hunting for a switch. Re-granting goes straight through `grantConsent`,
 * which means a visitor who changes their mind back does not have to wait
 * for a banner that will never reappear (their decision is stored).
 *
 * Withdrawal is effective, not cosmetic: `revokeConsent` deletes the analytics
 * id and sweeps Google's `_ga` / `_gcl_*` cookies, and the tag component
 * pushes an explicit denial to any gtag already loaded on the page.
 */
export function ConsentPreferences() {
  const t = useTranslations("legal.privacy");
  const locale = useLocale();
  const state = useConsentState();

  const decide = (granted: boolean) => {
    recordConsentDecision(
      granted ? grantConsent() : revokeConsent(),
      locale,
      capturedAttribution()?.utm_source,
    );
  };

  const status =
    state === "granted"
      ? t("consentStatusGranted")
      : state === "denied"
        ? t("consentStatusDenied")
        : t("consentStatusUndecided");

  return (
    <section id="cookievoorkeuren" className="scroll-mt-28">
      <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-3">
        {t("consentHeading")}
      </h2>
      <p>{t("consentIntro")}</p>
      <p className="mt-3">
        <span className="font-semibold text-foreground">{t("consentCurrentLabel")}</span>{" "}
        <span suppressHydrationWarning>{status}</span>
      </p>
      <div className="mt-4 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => decide(true)}
          className="px-4 py-2 text-xs font-medium border border-foreground text-foreground hover:bg-foreground hover:text-background transition-colors rounded-lg"
        >
          {t("consentAccept")}
        </button>
        <button
          type="button"
          onClick={() => decide(false)}
          className="px-4 py-2 text-xs font-medium border border-foreground text-foreground hover:bg-foreground hover:text-background transition-colors rounded-lg"
        >
          {t("consentWithdraw")}
        </button>
      </div>
    </section>
  );
}
